import {
  IonContent,
  IonButtons,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonToolbar,
  IonIcon,
  IonText,
  IonItem,
  IonInput,
  IonBadge,
  useIonAlert,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  IonGrid,
  IonRow,
  IonCol,
  IonCard,
  IonImg,
  IonCardHeader,
  IonCardTitle,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonSkeletonText,
  IonCardSubtitle,
  IonTitle,
  IonLabel,
  useIonModal,
  IonButton,
} from '@ionic/react';
import { arrowBackOutline, chevronDownCircleOutline, cubeOutline, fileTrayStackedOutline,searchOutline, ellipsisVerticalOutline, filterCircleSharp } from 'ionicons/icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RestApi } from '../services/Api';
import {
  loadList,
  ItemInventory as ItemType,
  LoadItem,
  ApiResponse,
  Category,
  Grouping,
} from "../models/Data";
import { InputChangeEventDetail, IonInfiniteScrollCustomEvent } from '@ionic/core';
import Detail from '../components/item/Detail';
import Filter from '../components/item/Filter';
import './Item.scss';

const useConstructor = (callBack = () => {}) => {
  const [hasBeenCalled, setHasBeenCalled] = useState(false);
  if (hasBeenCalled) return;
  callBack();
  setHasBeenCalled(true);
}

const Item: React.FC = () => {
  const [alert] = useIonAlert();
  const [isInfiniteDisabled, setInfiniteDisabled] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const [item, setItem] = useState<ItemType[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [groupings, setGroupings] = useState<Grouping[]>([]);
  const [itemId, setItemId] = useState<string>();
  const [dataItem, setDataItem] = useState<ApiResponse['data']['data']['items'] | null>(null);
  const { t } = useTranslation(["general"]);
  const [loading, setLoading] = useState(true);
  const [selectCategory, setSelectCategory] = useState<Category[]>([]);
  const [selectGrouping, setSelectGrouping] = useState<Grouping[]>([]);

  const massageAlert = async (status: string, body: string) => {
    alert({
      header: t(status),
      message: body,
      cssClass: `header-alert-${status}`,
      buttons: [
        {
          cssClass: "alert-button-confirm",
          text: t("yes"),
        },
      ],
    });
  };

  const doRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    setLoading(true);
    getItem(
      `item?keyword=${searchText}${
        selectCategory.length
          ? `&category_id=${selectCategory
              .map((c: Category) => c.category_id)
              .join(",")}`
          : ""
      }${
        selectGrouping.length
          ? `&grouping_id=${selectGrouping
              .map((c: Grouping) => c.grouping_id)
              .join(",")}`
          : ""
      }`,
      false
    );
    event.detail.complete();
  };

  const getItem = async (page: string, next: boolean) => {
    await RestApi(page, "get", {}, "application/json")
      .then(async (res: ApiResponse) => { 
        if (res.data.status) {
          setDataItem(res.data.data.items);
          setCategories(res.data.data.categories);
          setGroupings(res.data.data.groupings);
          if (next) {
            setItem((prevState: ItemType[]) => [
              ...prevState,
              ...res.data.data.items.data,
            ]);
          } else {
            setItem(res.data.data.items.data);
          }
        } else {
          massageAlert("error", res.data.message);
        }
        setLoading(false);
      })
      .catch((err: { message: string }) => {
        massageAlert("error", err.message);
        setLoading(false);
      });
  };

  const onChangeSearch = (e: CustomEvent<InputChangeEventDetail>) => {
    e.preventDefault();
    const value = e.detail.value ?? '';
    setSearchText(value);
    setLoading(true);
    getItem(
      `item?keyword=${searchText}${
        selectCategory.length
          ? `&category_id=${selectCategory
              .map((c: Category) => c.category_id)
              .join(",")}`
          : ""
      }${
        selectGrouping.length
          ? `&grouping_id=${selectGrouping
              .map((c: Grouping) => c.grouping_id)
              .join(",")}`
          : ""
      }`,
      false
    );
  };

  const loadData = (ev: IonInfiniteScrollCustomEvent<void>) => {
    if (dataItem?.current_page === dataItem?.last_page) {
      setInfiniteDisabled(true);
      return;
    }
    getItem(
      `item?page=${
        dataItem?.current_page ? dataItem.current_page + 1 : 1
      }&keyword=${searchText}${
        selectCategory.length
          ? `&category_id=${selectCategory
              .map((c: Category) => c.category_id)
              .join(",")}`
          : ""
      }${
        selectGrouping.length
          ? `&grouping_id=${selectGrouping
              .map((c: Grouping) => c.grouping_id)
              .join(",")}`
          : ""
      }`,
      true
    );
    (ev.target as HTMLIonInfiniteScrollElement).complete();
  };
  
  const [presentFilterModal, dismissFilterModal] = useIonModal(Filter, {
    items: {
      categories: categories,
      groupings: groupings,
    },
    selectedItems: {
      categories: selectCategory,
      groupings: selectGrouping,
    },
    onFilterCancel: () => dismissFilterModal(),
    onFilterChange: (selected: {
      categories: Category[];
      groupings: Grouping[];
    }) => {
      setSelectCategory(selected.categories);
      setSelectGrouping(selected.groupings);
      getItem(
        `item?keyword=${searchText}${
          selected.categories.length
            ? `&category_id=${selected.categories
                .map((c: Category) => c.category_id)
                .join(",")}`
            : ""
        }${
          selected.groupings.length
            ? `&grouping_id=${selected.groupings
                .map((c: Grouping) => c.grouping_id)
                .join(",")}`
            : ""
        }`,
        false
      );
      setLoading(true);
      dismissFilterModal();
    },
    multiple: true,
  });

  const [presentDetail, dismissDetail] = useIonModal(Detail, {
    item_id: itemId,
    onDismissDetail: () => {
      dismissDetail();
    },
  });
  
  useConstructor(async () => {
    getItem(
      `item?keyword=${searchText}`,
      false
    );
  });

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButton
            color="light"
            slot="start"
            fill="clear"
            routerLink="/home"
            routerDirection="back"
            className=""
            size="small"
          >
            <IonIcon color="light" src={arrowBackOutline} />
          </IonButton>
          <div className="search">
            <IonItem lines="full" className="item " slot="start">
              <IonIcon src={searchOutline} slot="start" />
              <IonInput
                placeholder={t("search") + " " + t("module.item")}
                className="input"
                clearInput={true}
                value={searchText}
                onIonChange={(e) => onChangeSearch(e)}
              ></IonInput>
            </IonItem>
          </div>
          <IonIcon
            color="light"
            slot="end"
            className="ion-no-padding ion-no-margin"
            size="large"
            icon={filterCircleSharp}
            onClick={() => {
              if (!loading) {
                presentFilterModal({
                  initialBreakpoint: 0.5,
                  breakpoints: [0, 0.5, 1.0],
                  handleBehavior: "cycle",
                  backdropDismiss: true,
                  id: "modal-filter",
                  onWillDismiss: async () => {
                    const modalElement = document.getElementById(
                      "modal-filter"
                    ) as HTMLIonModalElement;
                    if (modalElement) {
                      modalElement?.dismiss();
                    }
                  },
                });
              } else {
                massageAlert("info", t("loading"));
              }
            }}
          />
          <IonButtons slot="end" className="ion-no-padding ion-no-margin">
            <IonMenuButton className="ion-no-padding ion-no-margin">
              <IonIcon
                color="light"
                className="ion-no-padding ion-no-margin"
                icon={ellipsisVerticalOutline}
              />
            </IonMenuButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={`${t("loading")}`}
            refreshingSpinner="circles"
            refreshingText={`${t("loading")}`}
          ></IonRefresherContent>
        </IonRefresher>
        {loading ? (
          <IonGrid className="ion-no-padding">
            <IonRow>
              {loadList.map((value: LoadItem, key: number) => (
                <IonCol size="12" key={key}>
                  <IonCard className="ion-no-padding">
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "100%", height: "110px" }}
                    ></IonSkeletonText>
                    <IonCardHeader
                      className="ion-no-padding"
                      style={{
                        margin: "3px",
                      }}
                    >
                      <IonCardSubtitle className="ion-no-padding">
                        <IonBadge color="danger" style={{ width: "100%" }}>
                          <IonSkeletonText
                            animated={true}
                            style={{ width: "100%" }}
                          ></IonSkeletonText>
                        </IonBadge>
                      </IonCardSubtitle>
                      <IonCardTitle className="ion-no-padding">
                        <IonTitle size="small" className="ion-no-padding">
                          <IonSkeletonText
                            animated={true}
                            style={{ width: "100%" }}
                          ></IonSkeletonText>
                        </IonTitle>
                      </IonCardTitle>
                    </IonCardHeader>
                  </IonCard>
                </IonCol>
              ))}
            </IonRow>
          </IonGrid>
        ) : item?.length ? (
          <IonGrid>
            <IonRow className="item-card">
              {item.map((value: ItemType, key: number) => (
                <IonCol
                  size="12"
                  key={key}
                  onClick={() => {
                    setItemId(value.item_id);
                    presentDetail({
                      cssClass: "modal-fullscreen",
                    });
                  }}
                >
                  <IonCard>
                    <IonItem>
                      <IonText>
                        <p className="ion-no-padding ion-no-margin">
                          <strong>
                            {value.item_name} {value.item_unit} - (
                            {value.item_id})
                          </strong>
                        </p>
                      </IonText>
                    </IonItem>
                    <IonItem>
                      <IonLabel>
                        <small className="ion-no-padding ion-no-margin">
                          <strong>{value.category?.category_name}</strong>
                        </small>
                        <br />
                        <small className="ion-no-padding ion-no-margin">
                          <IonIcon
                            size="small"
                            color="dark"
                            src={cubeOutline}
                          ></IonIcon>
                          {" " + value.category?.grouping?.grouping_name}
                        </small>
                      </IonLabel>
                    </IonItem>
                    <IonItem>
                      <IonText>
                        <p className="ion-no-padding ion-no-margin">
                          <IonIcon
                            size="small"
                            color="dark"
                            src={fileTrayStackedOutline}
                          ></IonIcon>
                          <small>{` ${t("total")} ${t("in")} : ${
                            value.total_in
                          } `}</small>
                          <small>{`${t("total")} ${t("out")} : ${
                            value.total_out
                          } `}</small>
                          <small>{`${t("total")} ${t("stock")} : ${
                            value.total_stock
                          } `}</small>
                        </p>
                      </IonText>
                    </IonItem>
                  </IonCard>
                </IonCol>
              ))}
            </IonRow>
            <IonInfiniteScroll
              onIonInfinite={loadData}
              threshold="100px"
              disabled={isInfiniteDisabled}
            >
              {dataItem?.next_page_url && (
                <IonInfiniteScrollContent
                  loadingSpinner="bubbles"
                  loadingText={`${t("loading")}`}
                >
                  <IonRow>
                    <IonCol size="12">
                      <IonCard className="ion-no-padding">
                        <IonSkeletonText
                          animated={true}
                          style={{ width: "100%", height: "110px" }}
                        ></IonSkeletonText>
                        <IonCardHeader
                          className="ion-no-padding"
                          style={{
                            margin: "3px",
                          }}
                        >
                          <IonCardSubtitle className="ion-no-padding">
                            <IonBadge color="danger" style={{ width: "100%" }}>
                              <IonSkeletonText
                                animated={true}
                                style={{ width: "100%" }}
                              ></IonSkeletonText>
                            </IonBadge>
                          </IonCardSubtitle>
                          <IonCardTitle className="ion-no-padding">
                            <IonTitle size="small" className="ion-no-padding">
                              <IonSkeletonText
                                animated={true}
                                style={{ width: "100%" }}
                              ></IonSkeletonText>
                            </IonTitle>
                          </IonCardTitle>
                        </IonCardHeader>
                      </IonCard>
                    </IonCol>
                  </IonRow>
                </IonInfiniteScrollContent>
              )}
            </IonInfiniteScroll>
          </IonGrid>
        ) : dataItem != null ? (
          !item?.length ? (
            <IonCard>
              <IonImg src={"assets/icon/empty.svg"} />
              <IonText className="ion-text-center">
                <p>{t("not-found")}</p>
              </IonText>
            </IonCard>
          ) : (
            ""
          )
        ) : (
          <IonGrid className="ion-no-padding">
            <IonRow>
              {loadList.map((value: LoadItem, key: number) => (
                <IonCol size="12" key={key}>
                  <IonCard className="ion-no-padding">
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "100%", height: "110px" }}
                    ></IonSkeletonText>
                    <IonCardHeader
                      className="ion-no-padding"
                      style={{
                        margin: "3px",
                      }}
                    >
                      <IonCardSubtitle className="ion-no-padding">
                        <IonBadge color="danger" style={{ width: "100%" }}>
                          <IonSkeletonText
                            animated={true}
                            style={{ width: "100%" }}
                          ></IonSkeletonText>
                        </IonBadge>
                      </IonCardSubtitle>
                      <IonCardTitle className="ion-no-padding">
                        <IonTitle size="small" className="ion-no-padding">
                          <IonSkeletonText
                            animated={true}
                            style={{ width: "100%" }}
                          ></IonSkeletonText>
                        </IonTitle>
                      </IonCardTitle>
                    </IonCardHeader>
                  </IonCard>
                </IonCol>
              ))}
            </IonRow>
          </IonGrid>
        )}
      </IonContent>
    </IonPage>
  );
};

export default Item;
